// Parse a SWIFT MT940 statement (handles the bank's v1/v2 layouts + Banca Base's
// multi-line :86: blocks). :61: carries the movement; :86: carries the detail,
// often spread across continuation lines:
//   :86:<bankref>
//   BNF. <name> RFC. <rfc>
//   ACC. <counterparty clabe/account>
// We accumulate those lines and pull out the RFC, name, and counterparty account.
const toISO = (yymmdd) =>
  `20${yymmdd.slice(0, 2)}-${yymmdd.slice(2, 4)}-${yymmdd.slice(4, 6)}`;

// Amounts use European format: "70000,00" / "1.330,00".
const parseAmount = (s) =>
  parseFloat(String(s).replace(/\./g, "").replace(",", ".")) || 0;

// :60F:/:62F: balance lines: [C|D][yymmdd][CUR][amount] → signed booked balance.
const parseBalance = (s) => {
  const m = String(s).match(/^([CD])(\d{6})([A-Z]{3})([\d.,]+)/);
  if (!m) return null;
  const sign = m[1] === "D" ? -1 : 1;
  return { date: toISO(m[2]), currency: m[3], amount: sign * parseAmount(m[4]) };
};

// Pull payer/beneficiary detail out of the accumulated :86: text.
function parseDetail(detail) {
  const t = detail.replace(/\s+/g, " ").trim();
  const rfc = t.match(/RFC\.?\s*([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{2,3})\b/i);
  const name = t.match(/BNF\.?\s*(.+?)\s+RFC\b/i) || t.match(/ORD\.?\s*(.+?)\s+RFC\b/i);
  const acc = t.match(/ACC\.?\s*(\d{8,20})/i);
  const ref = t.match(/\b(\d{6,})\b/); // first long numeric token (bank reference)
  return {
    description: t,
    payerRfc: rfc && rfc[1].toUpperCase() !== "ND" ? rfc[1].toUpperCase() : null,
    payerName: name ? name[1].trim() : "",
    counterpartyClabe: acc ? acc[1] : null,
    reference: ref ? ref[1] : "",
  };
}

export function parseMT940(text) {
  const lines = String(text).replace(/^﻿/, "").split(/\r?\n/);
  const out = {
    account: null, statement: null, movements: [],
    openingBalance: null, closingBalance: null, periodStart: null, periodEnd: null,
  };
  let cur = null;
  let detail = null; // accumulating :86: text for `cur`

  const flush = () => {
    if (cur && detail != null) Object.assign(cur, parseDetail(detail));
    detail = null;
  };

  for (const raw of lines) {
    const ln = raw.trim();
    if (!ln) continue;
    const isTag = /^:\d{2}[A-Z]?:/.test(ln);

    if (!isTag) {
      // continuation line — belongs to the current :86: block
      if (detail != null) detail += " " + ln;
      continue;
    }
    if (ln.startsWith(":86:")) { flush(); detail = ln.slice(4); continue; }
    // any other tag ends an open :86: block
    flush();

    if (ln.startsWith(":25:")) out.account = ln.slice(4).trim();
    else if (ln.startsWith(":28C:")) out.statement = ln.slice(5).trim();
    else if (ln.startsWith(":60F:") || ln.startsWith(":60M:")) {
      const b = parseBalance(ln.slice(5));
      if (b) { out.openingBalance = b.amount; out.periodStart = b.date; }
    } else if (ln.startsWith(":62F:") || ln.startsWith(":62M:")) {
      const b = parseBalance(ln.slice(5));
      if (b) { out.closingBalance = b.amount; out.periodEnd = b.date; }
    } else if (ln.startsWith(":61:")) {
      const body = ln.slice(4);
      const m = body.match(/^(\d{6})(\d{4})?(RC|RD|C|D)([\d.,]+)N(.*)$/);
      if (m) {
        cur = {
          date: toISO(m[1]),
          postDate: null,
          sign: m[3],
          credit: m[3] === "C" || m[3] === "RC",
          reversal: m[3] === "RC" || m[3] === "RD",
          amount: parseAmount(m[4]),
          code: (m[5] || "").trim(),
          description: "",
          payerRfc: null, payerName: "", counterpartyClabe: null, reference: "",
        };
        out.movements.push(cur);
      }
    }
  }
  flush();
  return out;
}
